import { supabase } from './supabase';

// Bot notification types
type BotNotificationType = 'welcome' | 'birthday' | 'milestone' | 'streak';

interface BotNotification {
  type: BotNotificationType;
  userId: string;
  title: string;
  message: string;
  data?: Record<string, any>;
}

// Send a bot notification to a user
export async function sendBotNotification(notification: BotNotification) {
  try {
    await supabase.from('notifications').insert({
      user_id: notification.userId,
      type: 'system',
      title: notification.title,
      content: notification.message,
      data: {
        bot_type: notification.type,
        ...notification.data,
      },
    });
    return true;
  } catch (error) {
    console.error('Failed to send bot notification:', error);
    return false;
  }
}

// Welcome new user
export async function sendWelcomeNotification(userId: string, fullName: string) {
  return sendBotNotification({
    type: 'welcome',
    userId,
    title: 'Welcome to AUDC! 🎉',
    message: `Hey ${fullName}! Welcome to the Ambo University Developers Club. Start by completing your profile, joining the feed, and connecting with fellow developers. Happy coding!`,
  });
}

// Birthday wishes
export async function sendBirthdayNotification(userId: string, fullName: string) {
  return sendBotNotification({
    type: 'birthday',
    userId,
    title: 'Happy Birthday! 🎂',
    message: `Happy Birthday, ${fullName}! 🎈 The entire AUDC family wishes you an amazing day. May your code compile on the first try!`,
    data: { bonus_points: 50 },
  });
}

// Milestone notifications
export async function sendMilestoneNotification(
  userId: string,
  milestoneType: string,
  value: number
) {
  const milestones: Record<string, { title: string; message: string }> = {
    points_100: {
      title: '100 Points! 🌟',
      message: "You've reached 100 points! You're becoming an active contributor.",
    },
    points_500: {
      title: '500 Points! 🔥',
      message: "500 points milestone! You're on fire! Keep up the great work.",
    },
    points_1000: {
      title: '1000 Points! 🏆',
      message: "You've hit 1000 points! You're now a top contributor in AUDC!",
    },
    posts_10: {
      title: '10 Posts! ✍️',
      message: "You've written 10 posts! Your voice matters in this community.",
    },
    posts_50: {
      title: '50 Posts! 📝',
      message: '50 posts! You\'re a content creator extraordinaire!',
    },
    streak_7: {
      title: '7 Day Streak! 🔥',
      message: "A week-long streak! You're showing great dedication.",
    },
    streak_30: {
      title: '30 Day Streak! 💪',
      message: 'A whole month! Your consistency is inspiring.',
    },
    streak_100: {
      title: '100 Day Streak! 🏅',
      message: '100 days! You are a legend of dedication!',
    },
  };

  const milestone = milestones[milestoneType];
  if (!milestone) return false;

  return sendBotNotification({
    type: 'milestone',
    userId,
    title: milestone.title,
    message: milestone.message,
    data: { milestone_type: milestoneType, value },
  });
}

// Check and send milestone notifications
export async function checkMilestones(userId: string) {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('points, current_streak')
      .eq('id', userId)
      .single();

    if (!profile) return;

    const { data: sentMilestones } = await supabase
      .from('notifications')
      .select('data')
      .eq('user_id', userId)
      .eq('type', 'system')
      .contains('data', { bot_type: 'milestone' });

    const sentTypes = new Set(
      sentMilestones?.map((n: any) => n.data?.milestone_type).filter(Boolean) || []
    );

    // Check point milestones
    const pointMilestones = [
      { threshold: 100, type: 'points_100' },
      { threshold: 500, type: 'points_500' },
      { threshold: 1000, type: 'points_1000' },
    ];

    for (const { threshold, type } of pointMilestones) {
      if (profile.points >= threshold && !sentTypes.has(type)) {
        await sendMilestoneNotification(userId, type, profile.points);
      }
    }

    // Check streak milestones
    const streakMilestones = [
      { threshold: 7, type: 'streak_7' },
      { threshold: 30, type: 'streak_30' },
      { threshold: 100, type: 'streak_100' },
    ];

    for (const { threshold, type } of streakMilestones) {
      if ((profile.current_streak || 0) >= threshold && !sentTypes.has(type)) {
        await sendMilestoneNotification(userId, type, profile.current_streak || 0);
      }
    }
  } catch (error) {
    console.error('Error checking milestones:', error);
  }
}

// Check for birthdays (to be called by a scheduled job)
export async function checkBirthdays() {
  const today = new Date();
  const month = today.getMonth() + 1;
  const day = today.getDate();

  try {
    // Get users with birthday today
    // Note: This assumes you add a `birthday` column to profiles
    const { data: birthdayUsers } = await supabase
      .from('profiles')
      .select('id, full_name')
      .eq('birthday_month', month)
      .eq('birthday_day', day)
      .is('deleted_at', null);

    if (birthdayUsers) {
      for (const user of birthdayUsers) {
        // Check if we already sent birthday wish today
        const { data: existing } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', user.id)
          .eq('type', 'system')
          .contains('data', { bot_type: 'birthday' })
          .gte('created_at', today.toISOString().split('T')[0]);

        if (!existing || existing.length === 0) {
          await sendBirthdayNotification(user.id, user.full_name);
          
          // Add birthday bonus points
          await supabase.rpc('add_points', {
            user_id_param: user.id,
            points_param: 50,
            reason_param: 'Birthday bonus! 🎂',
          });
        }
      }
    }
  } catch (error) {
    console.error('Error checking birthdays:', error);
  }
}



